// Canal « Redox ratio » : rapport redox optique (Chance et al., 1979 ; Skala et al., 2007 ; Quinn et al., 2013),
// pixel par pixel, à partir des autofluorescences des coenzymes du métabolisme, sur deux scans de même grille de
// pixels :
// - NAD(P)H : excitation dans l'UV proche (330–390 nm, epi375 par défaut), FAD : excitation dans le bleu (430–500 nm,
//   epi470 par défaut) ;
// - intensités en comptes d'origine de la caméra (copie 8 bits ramenée par sa fenêtre d'origine), fond retiré : niveau
//   sans tissu (médiane sous le seuil d'Otsu), qui comprend le noir de la caméra et l'autofluorescence du verre ;
// - FAD multipliée par un facteur d'étalonnage (sensibilité relative des deux voies : puissance, exposition, filtres ;
//   1 : comptes bruts, rapport relatif à cet appareil) ;
// - rapport FAD / (FAD + NAD(P)H), borné à [0, 1], plus élevé = plus oxydé (convention de Chance et de Georgakoudi),
//   ou NAD(P)H / FAD (convention de Skala et Walsh) ; les deux images sont lissées (σ) avant : rapport de moyennes
//   locales, moins bruité qu'un rapport de pixels bruts ;
// - sans tissu (somme des deux intensités sous un seuil, Otsu par défaut) : noir ; ailleurs, rapport ramené à une
//   plage [bas, haut] (2e et 98e centiles par défaut) à travers une LUT, luminosité éventuellement modulée par
//   l'intensité totale (cartes redox usuelles : la teinte donne le rapport, la luminosité le signal ; racine carrée de
//   l'intensité rapportée au 95e centile du tissu, pour ne pas assombrir le tissu ordinaire).
// Fond, seuil, plage et intensité de référence à null : estimés sur un niveau réduit (redoxAuto), et réestimés quand
// les canaux, l'étalonnage ou la formule changent. Chiffres sur le tissu : moyenne et médiane du rapport, rapport des
// sommes.
import { VivLayerExtension } from '@hms-dbmi/viv';

import { otsu } from './blood';
import { lutGlsl } from './lut';
import { pixelSize } from '../../services';

export const REDOX_ID = 'redox';  // identifiant du canal parmi les modalités
// n, f : ids des images NAD(P)H et FAD ; formula : 'fad' (FAD / (FAD + NAD(P)H)) ou 'nadh' (NAD(P)H / FAD) ; calib :
// facteur appliqué à FAD ; smooth : σ en px pleine résolution ; bgN, bgF, threshold, bright : comptes d'origine ;
// lo, hi : plage affichée du rapport ; weighted : luminosité = intensité totale.
export const REDOX_DEFAULTS = {
  n: null, f: null, formula: 'fad', calib: 1, smooth: 1, bgN: null, bgF: null, threshold: null, lo: null, hi: null,
  bright: null, weighted: true, lut: 'mpl-viridis', lutRange: null,
};
export const REDOX_AUTO_KEYS = ['bgN', 'bgF', 'threshold', 'lo', 'hi', 'bright'];  // null : automatiques
export const REDOX_FALLBACK = { fad: { lo: 0, hi: 1 }, nadh: { lo: 0, hi: 4 } };
export const REDOX_FORMULAS = { fad: 'FAD / (FAD + NAD(P)H)', nadh: 'NAD(P)H / FAD' };

// Longueur d'onde d'excitation d'une fluorescence d'après sa modalité (« epi375 » -> 375), sinon null.
export const excitationOf = ch => {
  const m = (ch?.modality || '').match(/^epi[_-]?(\d{3})/i);
  return m ? Number(m[1]) : null;
};
export const isNadh = ch => { const nm = excitationOf(ch); return nm >= 330 && nm <= 390; };
export const isFad = ch => { const nm = excitationOf(ch); return nm >= 430 && nm <= 500; };

const modules = new Map();
// Module de shader pour une LUT (recompilé seulement quand la LUT change) ; canaux : NAD(P)H et FAD en comptes,
// fond retiré (contraste [fond, fond + 1 compte]).
function moduleFor(lutId) {
  if (modules.has(lutId)) return modules.get(lutId);
  const name = `redoxModule${modules.size}`;
  const module = {
    name,
    uniformTypes: {
      calib: 'f32', formula: 'f32', threshold: 'f32', lo: 'f32', hi: 'f32', bright: 'f32', weighted: 'f32', lutLo: 'f32', lutHi: 'f32',
    },
    fs: `
uniform ${name}Uniforms {
  float calib;
  float formula;
  float threshold;
  float lo;
  float hi;
  float bright;
  float weighted;
  float lutLo;
  float lutHi;
} ${name};
${lutGlsl('redox_lut', lutId)}

vec3 redox_color(float[NUM_CHANNELS] intensity) {
  float n = max(intensity[0], 0.);
  float f = max(intensity[1], 0.) * ${name}.calib;
  float s = n + f;
  if (s < ${name}.threshold) return vec3(0.);  // sans tissu
  float r = ${name}.formula < 0.5 ? f / max(s, 1e-6) : n / max(f, 1e-6);
  vec3 c = redox_lut(mix(${name}.lutLo, ${name}.lutHi, clamp((r - ${name}.lo) / max(${name}.hi - ${name}.lo, 1e-6), 0., 1.)));
  float w = ${name}.weighted > 0.5 ? sqrt(clamp(s / max(${name}.bright, 1e-6), 0., 1.)) : 1.;
  return c * w;
}`,
    inject: { 'fs:DECKGL_MUTATE_COLOR': 'rgba = vec4(redox_color(intensity), 1.0);' },
  };
  modules.set(lutId, module);
  return module;
}
const extensionOf = layer => (layer.props.extensions || []).find(e => e instanceof RedoxExtension);

// Réglages résolus lus dans la prop « redox » du calque : un changement de curseur ne met à jour que les uniformes. La
// LUT est une option de l'extension : en changer crée une nouvelle extension.
export class RedoxExtension extends VivLayerExtension {
  getVivShaderTemplates() { return { modules: [moduleFor(extensionOf(this)?.opts.lut)] }; }

  updateState(params, extension) {
    super.updateState(params, extension);
    const r = this.props.redox || {};
    const uniforms = {
      calib: r.calib ?? 1, formula: r.formula === 'nadh' ? 1 : 0, threshold: r.threshold ?? 0, lo: r.lo ?? 0,
      hi: r.hi ?? 1, bright: r.bright ?? 1, weighted: r.weighted ? 1 : 0,
      lutLo: r.lutRange?.[0] ?? 0, lutHi: r.lutRange?.[1] ?? 1,  // partie de la LUT utilisée
    };
    for (const model of this.getModels()) model.shaderInputs.setProps({ [moduleFor(extension.opts.lut).name]: uniforms });
  }
}
RedoxExtension.extensionName = 'RedoxExtension';

// Niveau réduit d'au moins minPixels pixels.
function smallLevel(loader, minPixels) {
  let level = loader.length - 1;
  while (level > 0) {
    const { width, height } = pixelSize(loader[level]);
    if (width * height >= minPixels) break;
    level -= 1;
  }
  return level;
}

// NAD(P)H et FAD en comptes d'origine sur un niveau réduit (au moins un million de pixels), lus une fois ; toOriginal :
// conversion d'une valeur lue (copie 8 bits) en comptes d'origine, null si identité.
export async function redoxData(loaderN, loaderF, toOriginalN, toOriginalF) {
  const level = smallLevel(loaderN, 1e6);
  const selection = { t: 0, z: 0, c: 0 };
  const [rn, rf] = await Promise.all([loaderN, loaderF].map(l => l[Math.min(level, l.length - 1)].getRaster({ selection })));
  const convert = (data, to) => Float32Array.from(data, v => (to ? to(v) : v));
  return { n: convert(rn.data, toOriginalN), f: convert(rf.data, toOriginalF) };
}

const quantile = (sorted, q) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * q))];
const backgroundOf = data => {
  const t = otsu(data);
  return quantile(Float64Array.from(data.filter(v => v <= t)).sort(), 0.5) ?? 0;
};
const ratioOf = (n, f, formula) => (formula === 'nadh' ? n / Math.max(f, 1e-6) : f / Math.max(n + f, 1e-6));
// Pixel retenu pour les chiffres : sur le tissu et, pour NAD(P)H / FAD, FAD d'au moins 5 % du signal (rapport ≤ 19 :
// au-delà, un rapport à un bruit proche de zéro emporterait la moyenne).
const counted = (n, f, s) => n + f >= s.threshold && (s.formula !== 'nadh' || f >= 0.05 * (n + f));

// Valeurs automatiques, en tenant compte de celles fixées par l'utilisateur (fond, seuil) : fond de chaque voie, seuil
// du tissu (Otsu de la somme), plage (2e et 98e centiles du rapport sur le tissu), intensité de référence (95e centile
// de la somme sur le tissu). null si trop peu de tissu.
export function redoxAuto({ n, f }, s) {
  const bgN = s.bgN ?? backgroundOf(n);
  const bgF = s.bgF ?? backgroundOf(f);
  const sum = new Float32Array(n.length);
  for (let i = 0; i < n.length; i += 1) sum[i] = Math.max(0, n[i] - bgN) + Math.max(0, f[i] - bgF) * s.calib;
  const threshold = s.threshold ?? otsu(sum);
  const ratios = [];
  const sums = [];
  for (let i = 0; i < n.length; i += 1) {
    const vn = Math.max(0, n[i] - bgN);
    const vf = Math.max(0, f[i] - bgF) * s.calib;
    if (!counted(vn, vf, { ...s, threshold })) continue;
    ratios.push(ratioOf(vn, vf, s.formula));
    sums.push(sum[i]);
  }
  if (ratios.length < 100) return null;
  const r = Float64Array.from(ratios).sort();
  const round = (v, d) => Math.round(v * 10 ** d) / 10 ** d;
  return {
    bgN: Math.round(bgN), bgF: Math.round(bgF), threshold: Math.round(threshold),
    lo: round(quantile(r, 0.02), 3), hi: round(quantile(r, 0.98), 3),
    bright: Math.round(quantile(Float64Array.from(sums).sort(), 0.95)),
  };
}

// Chiffres sur le tissu (réglages résolus) : part de l'image, moyenne et médiane du rapport pixel à pixel, rapport des
// sommes (FAD totale / (FAD + NAD(P)H) totales, ou NAD(P)H / FAD).
export function redoxStats({ n, f }, s) {
  const ratios = [];
  let sumN = 0;
  let sumF = 0;
  for (let i = 0; i < n.length; i += 1) {
    const vn = Math.max(0, n[i] - s.bgN);
    const vf = Math.max(0, f[i] - s.bgF) * s.calib;
    if (!counted(vn, vf, s)) continue;
    ratios.push(ratioOf(vn, vf, s.formula));
    sumN += vn;
    sumF += vf;
  }
  if (!ratios.length) return null;
  const r = Float64Array.from(ratios).sort();
  return {
    area: ratios.length / n.length, mean: r.reduce((a, v) => a + v, 0) / r.length, median: quantile(r, 0.5),
    pooled: ratioOf(sumN, sumF, s.formula),
  };
}

// Réglages résolus : ceux de l'utilisateur, sinon les valeurs automatiques, sinon les valeurs usuelles.
export const resolveRedox = (settings, auto) => ({
  ...settings,
  ...Object.fromEntries(REDOX_AUTO_KEYS.map(key => [key, settings[key] ?? auto?.[key] ?? REDOX_FALLBACK[settings.formula]?.[key] ?? null])),
});
